import _ from 'lodash';
import { ReportTemplateFormFields } from '@components/Template/AdditionalInfo/ReportTemplateModal/ReportTemplateModal';
import { useCallback, useEffect } from 'react';
import { useTemplateBi } from './useTemplateBi';
import { ACTIONS, useTemplateContext } from '../Contexts/TemplateContext';
import { useErrorMonitor, useHttpClient } from '@wix/fe-essentials-standalone';
import { REPORT_TEMPLATE_API_URL } from '@utils/constants';

export const useReportTemplateForm = ({ listingId }: { listingId: string }) => {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const { logBiClick } = useTemplateBi({ is_mobile: false });
  const { dispatch, reportTemplateData } = useTemplateContext();

  useEffect(() => {
    if (reportTemplateData.loading === false) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [reportTemplateData.loading]);

  const onSubmit = useCallback(
    async (data?: ReportTemplateFormFields) => {
      dispatch({
        type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
        payload: {
          loading: true,
        },
      });
      const { reportFormValues } = reportTemplateData;
      if (data) {
        dispatch({
          type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
          payload: { reportFormValues: data },
        });
      }
      const values = data || reportFormValues;
      logBiClick({
        clickType: 'Report template modal - Submit report',
        click_data: {
          type_of_issue: values?.issueType,
          describe_issue: values?.issueDescription,
          agree_to_be_contact: values?.agreeToBeContacted,
        },
      });
      dispatch({
        type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
        payload: {
          showReportTemplateError: false,
        },
      });
      try {
        await httpClient.post(REPORT_TEMPLATE_API_URL, {
          listingId,
          issueType: values.issueType,
          reportText: values.issueDescription,
          agreeToBeContacted: values.agreeToBeContacted,
        });
        dispatch({
          type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
          payload: {
            openReportTemplateModal: false,
            showReportTemplateError: false,
            showReportTemplateSuccessMsg: true,
            loading: false,
          },
        });
      } catch (error) {
        errorMonitor.captureException(error as Error);
        dispatch({
          type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
          payload: {
            openReportTemplateModal: false,
            showReportTemplateError: true,
            loading: false,
          },
        });
      }
    },
    [dispatch, httpClient, logBiClick, reportTemplateData, listingId],
  );

  const openReportModal = useCallback(() => {
    logBiClick({ clickType: 'Report this template' });
    dispatch({
      type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
      payload: {
        openReportTemplateModal: true,
        reportFormValues: {
          issueType: '',
          issueDescription: '',
          agreeToBeContacted: false,
        },
      },
    });
  }, [dispatch]);

  const onClose = useCallback(
    (clickType: string) => {
      dispatch({
        type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
        payload: {
          openReportTemplateModal: false,
        },
      });
      logBiClick({
        clickType,
      });
    },
    [dispatch, logBiClick],
  );

  const resetFormValues = useCallback(() => {
    dispatch({
      type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
      payload: {
        reportFormValues: {
          issueType: '',
          issueDescription: '',
          agreeToBeContacted: false,
        },
      },
    });
  }, [dispatch]);

  return {
    onSubmit,
    onClose,
    openReportModal,
    resetFormValues,
  };
};
